import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Boxes, Building2, Package, Send, ChevronDown, Loader2, RefreshCw, AlertTriangle, Eye,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchCapitalCCList,
    fetchCapitalItemCodes,
    fetchCapitalStockGrid,
    submitCapitalStockIssue,
    clearItemCodeList,
    clearGrid,
    clearSubmitResult,
    resetCapitalStockIssue,
    selectCSICCList,
    selectCSIItemCodeList,
    selectCSIGridItems,
    selectCSISubmitResult,
    selectCSILoading,
    selectCSIErrors,
} from '../../slices/purchaseSlice/capitalStockIssueSlice';


// Date picker values are JS Dates; sent raw they serialise as UTC ("…T18:30:00Z" = previous day in IST).
// Send the local calendar date instead; strings (already formatted) pass through unchanged.
const toApiDate = (v) => {
    if (!v) return '';
    if (v instanceof Date) {
        return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
    }
    return v;
};

// ── Shared UI primitives (mirrors DailyIssue / ItemsTransfer pages) ─────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const SUBMIT_TYPES = [
    { value: '1', label: 'Save & Add further' },
    { value: '2', label: 'Convert to Finish' },
];

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// Mirrors legacy ValidateAmountQuantity: digits, one decimal point, max 3 fraction digits.
const isValidQtyString = (v) => {
    if (v === '') return true;
    if (!/^\d*\.?\d{0,3}$/.test(v)) return false;
    return true;
};

const CapitalStockIssue = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';
    const createdBy = userData?.userName || userData?.employeeId || uid || 'system';

    const ccList       = useSelector(selectCSICCList);
    const itemCodeList = useSelector(selectCSIItemCodeList);
    const gridItems     = useSelector(selectCSIGridItems);
    const submitResult  = useSelector(selectCSISubmitResult);
    const loading        = useSelector(selectCSILoading);
    const errors          = useSelector(selectCSIErrors);

    const [selectedCC, setSelectedCC] = useState('');
    const [selectedItemCode, setSelectedItemCode] = useState('');

    const [checkedRows, setCheckedRows] = useState({});
    const [qtyEdits, setQtyEdits] = useState({});

    const [issueDate, setIssueDate] = useState(todayFormatted());
    const [remarks, setRemarks] = useState('');
    const [submitType, setSubmitType] = useState('');

    const [showConvertConfirm, setShowConvertConfirm] = useState(false);

    useEffect(() => {
        if (uid && roleId) dispatch(fetchCapitalCCList({ uid, rid: roleId }));
        return () => dispatch(resetCapitalStockIssue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset per-row state whenever the grid is (re)loaded
    useEffect(() => {
        setCheckedRows({});
        setQtyEdits({});
    }, [gridItems]);

    useEffect(() => {
        if (submitResult) {
            toast.success(`The Transaction Id Is :- ${submitResult.code}   Generated Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center / Master Item Code selection ────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setSelectedItemCode('');
        dispatch(clearItemCodeList());
        dispatch(clearGrid());
        if (cc && uid && roleId) dispatch(fetchCapitalItemCodes({ uid, rid: roleId, ccCode: cc }));
    };

    const handleItemCodeChange = (code) => {
        setSelectedItemCode(code);
        dispatch(clearGrid());
    };

    const handleClearItemCode = () => {
        setSelectedItemCode('');
        dispatch(clearGrid());
    };

    const handleView = () => {
        if (!selectedItemCode) { toast.error('Select master item code'); return; }
        if (!selectedCC)       { toast.error('Select Cost/Job Center Code'); return; }
        dispatch(fetchCapitalStockGrid({ masterItemCode: selectedItemCode, ccCode: selectedCC }));
    };

    // ── Grid row edits ───────────────────────────────────────────────────────

    const toggleRowChecked = (rid) => setCheckedRows((prev) => ({ ...prev, [rid]: !prev[rid] }));

    const handleQtyChange = (row, value) => {
        if (!isValidQtyString(value)) return;
        if (value !== '' && num(value) > num(row.BalanceQty)) {
            toast.error('Invalid Quantity');
            setQtyEdits((prev) => ({ ...prev, [row.Rid]: '' }));
            return;
        }
        setQtyEdits((prev) => ({ ...prev, [row.Rid]: value }));
    };

    const totalToIssue = useMemo(
        () => gridItems.reduce((sum, row) => sum + num(qtyEdits[row.Rid]), 0),
        [gridItems, qtyEdits]
    );

    // ── Submit ───────────────────────────────────────────────────────────────

    const buildPayload = (alertType) => {
        let rids = '', issueQtys = '', basics = '';
        gridItems.forEach((row) => {
            rids      += `${row.Rid},`;
            issueQtys += `${qtyEdits[row.Rid]},`;
            basics    += `${row.Basic},`;
        });
        const payload = {
            Rid: rids,
            IssueQtys: issueQtys,
            Date: toApiDate(issueDate),
            Remarks: remarks,
            MasterItemCode: selectedItemCode,
            SubmitType: submitType,
            Basic: basics,
            CostCenter: selectedCC,
            RoleID: String(roleId),
            Createdby: createdBy,
        };
        if (alertType) payload.AlertType = alertType;
        return payload;
    };

    const validateBeforeSubmit = () => {
        if (!selectedCC)        { toast.error('Select Cost/Job Center Code'); return false; }
        if (!selectedItemCode)  { toast.error('Select Master ItemCode'); return false; }
        if (!issueDate)          { toast.error('Select Date'); return false; }
        if (!remarks.trim())     { toast.error('Select Remarks'); return false; }
        if (!submitType)          { toast.error('Select Submission Type'); return false; }
        if (gridItems.length === 0) { toast.error('Please Add Daily Issue Items'); return false; }
        if (!gridItems.every((r) => checkedRows[r.Rid])) { toast.error('Please Verify Items'); return false; }
        if (!gridItems.every((r) => qtyEdits[r.Rid] !== undefined && qtyEdits[r.Rid] !== '')) {
            toast.error('Enter Quantity to Issue');
            return false;
        }
        if (submitType === '1' && !(totalToIssue > 0)) {
            toast.error('Invalid Submission');
            return false;
        }
        return true;
    };

    const handleSubmit = () => {
        if (!validateBeforeSubmit()) return;
        if (submitType === '2') {
            setShowConvertConfirm(true);
            return;
        }
        dispatch(submitCapitalStockIssue(buildPayload()));
    };

    const handleConvertConfirm = (alertType) => {
        setShowConvertConfirm(false);
        dispatch(submitCapitalStockIssue(buildPayload(alertType)));
    };

    const handleReset = () => {
        setSelectedCC('');
        setSelectedItemCode('');
        setCheckedRows({});
        setQtyEdits({});
        setIssueDate(todayFormatted());
        setRemarks('');
        setSubmitType('');
        dispatch(clearItemCodeList());
        dispatch(clearGrid());
        if (uid && roleId) dispatch(fetchCapitalCCList({ uid, rid: roleId }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = gridItems.length > 0;
    const checkedCount = gridItems.filter((r) => checkedRows[r.Rid]).length;

    const sumAvailable = useMemo(() => gridItems.reduce((s, r) => s + num(r.AvailableQty), 0), [gridItems]);
    const sumIssue      = useMemo(() => gridItems.reduce((s, r) => s + num(r.IssueQty), 0), [gridItems]);
    const sumBalance    = useMemo(() => gridItems.reduce((s, r) => s + num(r.BalanceQty), 0), [gridItems]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <Boxes className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Stock Conversion</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Convert capital master item stock into issuable cost-center stock.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Cost Center + Master Item Code selection ────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center &amp; Master Item" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Cost/Job Center Code</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.ccList}>
                                <option value="">-Please Select-</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>

                    {selectedCC && (
                        <div className="flex-1 min-w-[240px]">
                            <Label required>Master Item Code</Label>
                            <div className="relative">
                                <select className={inputCls} value={selectedItemCode} onChange={(e) => handleItemCodeChange(e.target.value)} disabled={loading.itemCodeList}>
                                    <option value="">-Please Select-</option>
                                    {itemCodeList.map((ic) => (
                                        <option key={ic.ItemCode} value={ic.ItemCode}>{ic.ItemName}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    {loading.itemCodeList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedCC && (
                        <div className="flex items-center gap-2">
                            <button onClick={handleView} disabled={loading.grid}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.grid ? <SpinnerIcon /> : <Eye className="w-4 h-4" />} View
                            </button>
                            <button onClick={handleClearItemCode}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                Clear
                            </button>
                        </div>
                    )}
                </div>
            </SectionCard>

            {/* ── Stock conversion items grid ──────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Package} title="Stock Conversion Items"
                    subtitle={`${checkedCount} of ${gridItems.length} item${gridItems.length !== 1 ? 's' : ''} verified`}
                    accent="indigo">
                    <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                    {['Chk', 'Mrr No', 'Item Code', 'Item Name', 'Specification', 'Units', 'Basic', 'Actuall Quantity', 'Issued Quantity', 'Balance Quantity', 'Quantity to Issue'].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gridItems.map((row, idx) => (
                                    <tr key={row.Rid ?? idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                        <td className="px-2.5 py-2">
                                            <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                checked={!!checkedRows[row.Rid]} onChange={() => toggleRowChecked(row.Rid)} />
                                        </td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.MrrNo}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{row.ItemCode}</td>
                                        <td className="px-2.5 py-2 text-gray-900 dark:text-white">{row.ItemName}</td>
                                        <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{row.Specification}</td>
                                        <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{row.Units}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300">{row.Basic}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.AvailableQty}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.IssueQty}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.BalanceQty}</td>
                                        <td className="px-1.5 py-1.5 w-28">
                                            <input type="text" inputMode="decimal"
                                                className={`${inputCls} text-right`}
                                                value={qtyEdits[row.Rid] ?? ''}
                                                onChange={(e) => handleQtyChange(row, e.target.value)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-indigo-50/60 dark:bg-indigo-900/20 font-semibold text-gray-700 dark:text-gray-200">
                                    <td colSpan={7}></td>
                                    <td className="px-2.5 py-2 text-right">{sumAvailable.toFixed(3)}</td>
                                    <td className="px-2.5 py-2 text-right">{sumIssue.toFixed(3)}</td>
                                    <td className="px-2.5 py-2 text-right">{sumBalance.toFixed(3)}</td>
                                    <td className="px-2.5 py-2 text-right">{totalToIssue.toFixed(3)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label required>Date</Label>
                            <CustomDatePicker value={issueDate} onChange={setIssueDate}
                                format="DD-MMM-YYYY" placeholder="Select Date" maxDate={todayFormatted()} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                        <div>
                            <Label required>Submit Type</Label>
                            <select className={inputCls} value={submitType} onChange={(e) => setSubmitType(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {SUBMIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}

            {/* ── "quantity still available" confirm modal — SubmitType 2 ─────── */}
            {showConvertConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                            <h3 className="font-bold text-gray-900 dark:text-white">Convert to Finish?</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            There are still quantity available under this item, do you want to convert the item to stock?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => handleConvertConfirm('No')} disabled={loading.submit}
                                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold disabled:opacity-50">
                                No
                            </button>
                            <button onClick={() => handleConvertConfirm('Yes')} disabled={loading.submit}
                                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.submit ? 'Submitting…' : 'Yes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CapitalStockIssue;
